import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {Object} ScrollTextRevealRefs
 * @property {HTMLElement} stage
 */

/** @extends {Component<ScrollTextRevealRefs>} */
export class ScrollTextRevealComponent extends Component {
  requiredRefs = ['stage'];

  /** @type {number | null} */
  #scrollRafId = null;
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {boolean} */
  #isInView = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    if (prefersReducedMotion()) {
      this.style.setProperty('--reveal-progress', '1');
      return;
    }

    window.addEventListener('scroll', this.#handleScroll, { passive: true, signal });
    window.addEventListener('resize', this.#handleScroll, { passive: true, signal });

    this.#observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;

        this.#isInView = entry.isIntersecting;
        if (entry.isIntersecting) {
          this.#updateProgress();
        }
      },
      { root: null, rootMargin: '100% 0px 100% 0px', threshold: 0 }
    );

    this.#observer.observe(this);
    signal.addEventListener('abort', () => this.#observer?.disconnect());

    this.#updateProgress();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #handleScroll = () => {
    if (this.#scrollRafId !== null || !this.#isInView) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateProgress();
    });
  };

  #updateProgress = () => {
    const stage = this.refs.stage;
    if (!stage) return;

    const viewportHeight = window.innerHeight || 1;
    const rect = stage.getBoundingClientRect();
    const elementCenterY = rect.top + rect.height / 2;

    const scrollStart = (Number(this.dataset.scrollStart ?? 115) / 100) * viewportHeight;
    const scrollEnd = (Number(this.dataset.scrollEnd ?? 25) / 100) * viewportHeight;
    const range = scrollStart - scrollEnd;

    if (range <= 0) {
      this.style.setProperty('--reveal-progress', '1');
      return;
    }

    const rawProgress = Math.min(1, Math.max(0, (scrollStart - elementCenterY) / range));
    const progress = this.#applyEasing(rawProgress, this.dataset.scrollEasing ?? 'smooth');

    this.style.setProperty('--reveal-progress', String(progress));
  };

  /**
   * @param {number} progress
   * @param {string} easing
   * @returns {number}
   */
  #applyEasing(progress, easing) {
    if (easing === 'linear') return progress;

    // Smoothstep — slow start and end for a more cinematic scrub
    return progress * progress * (3 - 2 * progress);
  }
}

if (!customElements.get('scroll-text-reveal-component')) {
  customElements.define('scroll-text-reveal-component', ScrollTextRevealComponent);
}
